import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import "../Component2/css/Signup.css";
import logo from "../Component2/Image/logo2.png";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { BASE_URL } from "../config/constants";
import { saveFcmToken } from "./saveFcmToken";
import ReCAPTCHA from "react-google-recaptcha";
import SEO from "../SEO";
import { seoConfig } from "../config/seoConfig";
import { parseApiResponse } from "../config/parseApiResponse";
import SignupVideoModal from "./SignupVideoModal";

const Signup = () => {
  const navigate = useNavigate();

  // ✅ FIX 1: activeRole state
  const [activeRole, setActiveRole] = useState("candidate");
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    const lockedRole = localStorage.getItem("signupRole");
    if (lockedRole) {
      setActiveRole(lockedRole);
    }
  }, []);

  /* ---------------- VALIDATION ---------------- */

  const validationSchema = Yup.object({
    fullname: Yup.string().required("Full name is required"),

    email: Yup.string()
      .email("Invalid email")
      .required("Work email is required"),

    mobile: Yup.string()
      .required("Mobile number is required")
      .matches(/^[6-9]\d{9}$/, "Mobile number must be exactly 10 digits"),

    password: Yup.string()
      .min(6, "Password must be at least 6 characters")
      .required("Password is required"),

    terms: Yup.boolean().oneOf([true], "You must accept Terms & Conditions"),
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const onSubmit = async (values) => {
    if (!captchaToken) {
      toast.error("Please verify captcha!");
      return;
    }

    setLoading(true);

    try {
      let url = "";
      let payload = {};

      // ✅ Role wise API + payload
      if (activeRole === "candidate") {
        url = `${BASE_URL}candidate/signup/tbl_candidate`;
        payload = {
          can_name: values.fullname,
          can_email: values.email,
          can_password: values.password,
          can_mobile: values.mobile,
          can_status: "Active",
          can_pay_status: "Pending",
          captchaToken: captchaToken,
        };
      } else {
        url = `${BASE_URL}employer/signup/tbl_employer`;
        payload = {
          emp_name: values.fullname,
          emp_email: values.email,
          emp_password: values.password,
          emp_mobile: values.mobile,
          emp_status: "Active",
          emp_pay_status: "Pending",
          captchaToken: captchaToken,
        };
      }

      // ✅ API call
      const response = await axios.post(url, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      const res = parseApiResponse(response);

      if (res.status === true) {
        console.log("REGISTER SUCCESS", res);

        toast.success("Signup successful! OTP sent to your email ✅");

        // ✅ Candidate FCM
        if (activeRole === "candidate" && res.data?.can_id) {
          saveFcmToken(res.data.can_id);
        }

        // 🔹 TEMP STORAGE
        localStorage.setItem(
          "signupTempData",
          JSON.stringify({
            role: activeRole,
            data: res.data,
            createdAt: Date.now(),
          }),
        );

        // ✅ VERIFY USER
        localStorage.setItem(
          "verifyUser",
          JSON.stringify({
            email: values.email,
            role: activeRole,
            mobile: values.mobile,
          }),
        );

        // ✅ PAYMENT USER
        localStorage.setItem(
          "paymentUser",
          JSON.stringify({
            name: values.fullname,
            email: values.email,
            role: activeRole,
            mobile: values.mobile,
            returnTo: activeRole === "candidate" ? "/profile" : "/emp-profile",
          }),
        );

        localStorage.removeItem("signupRole");

        reset();

        // ✅ REDIRECT
        setTimeout(() => {
          navigate("/verify");
        }, 1000);
      } else {
        console.log("REGISTER ERROR", res);
        toast.error(res.message || "Signup failed");
      }
    } catch (error) {
      console.log("ERROR:", error);

      if (error.response) {
        toast.error(error.response.data.message || "Server error");
      } else if (error.request) {
        toast.error("Server not responding");
      } else {
        toast.error("Unexpected error");
      }
    } finally {
      setLoading(false);
    }
  };

  const termsRoute =
    activeRole === "candidate"
      ? "/candidate-terms-condition"
      : "/employer-terms-condition";

  const termsLabel =
    activeRole === "candidate"
      ? "Candidate Terms & Conditions"
      : "Employer Terms & Conditions";

  useEffect(() => {
    const role = localStorage.getItem("signupRole");
    const completed = localStorage.getItem("videoCompleted");

    if (!role || completed !== "true") {
      navigate("/signin");
    }
  }, []);

  /* ---------------- JSX ---------------- */
  return (
    <>
      {showVideo && (
        <SignupVideoModal
          show={true}
          forceRole="candidate"
          onClose={() => setShowVideo(false)}
        />
      )}

      <SEO
        title={seoConfig.signup.title}
        description={seoConfig.signup.description}
      />
      {/* TOAST */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        pauseOnHover
        theme="colored"
      />

      <div className="min-vh-100 d-flex align-items-center justify-content-center p-3 mt-3 mb-5 ms-2 me-2">
        <div
          className="row g-0 shadow-lg rounded-4 overflow-hidden bg-white"
          style={{ maxWidth: "1000px" }}
        >
          {/* LEFT PANEL */}
          <div className="col-lg-6 d-none d-lg-flex flex-column justify-content-between signup-brand-panel">
            {" "}
            <div>
              <h2 className="fw-bold">Pharma Jobs</h2>
              <h4 className="fw-semibold">
                Build your hiring{" "}
                <span style={{ color: "#ffd60a" }}>in minutes.</span>
              </h4>
              <p className="small opacity-75">
                Set up profile, post jobs, or start applying.
              </p>

              <div className="mt-4">
                <span className="badge bg-dark rounded-pill p-3 mb-2 border-0">
                  Step 1 – 1 min
                </span>
                <br />
                <span className="badge bg-warning text-dark rounded-pill p-3 border-0">
                  Step 2 – Instant
                </span>
              </div>
            </div>
            <p className="small opacity-75">
              Pharma Jobs helped us close roles 2x faster.”
            </p>
          </div>

          {/* RIGHT FORM */}
          <div className="col-lg-6 signup-form-side">
            {" "}
            <div className="signup-logo text-center mb-3">
              {" "}
              <NavLink to="/">
                <img
                  src={logo}
                  // style={{ width: "400px", height: "70px" }}
                  alt="logo"
                />
              </NavLink>
            </div>
            <div className="Create-account-header">
              <div>
                <h2 className="signup-title">Create Your Account</h2>

                <p className="signup-subtitle">
                  Join Pharma Jobs & start your journey
                </p>
              </div>

              <NavLink to="/signin" className="btn btn-sm btn-outline-auth">
                <b>Login</b>
              </NavLink>
            </div>
            {/* FORM */}
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="row g-2">
                <div className="col-md-6">
                  <label className="mb-1">Full name</label>
                  <input
                    className={`form-control ${
                      errors.fullname ? "is-invalid" : ""
                    }`}
                    {...register("fullname")}
                    placeholder="Enter name"
                  />
                  <div className="invalid-feedback">
                    {errors.fullname?.message}
                  </div>
                </div>

                <div className="col-md-6">
                  <label className="mb-1">Email</label>
                  <input
                    className={`form-control ${
                      errors.email ? "is-invalid" : ""
                    }`}
                    {...register("email")}
                    placeholder="Enter email"
                  />
                  <div className="invalid-feedback">
                    {errors.email?.message}
                  </div>
                </div>
              </div>

              <div className="row g-2 mt-1">
                <div className="col-md-6">
                  <label className="mb-1">Mobile Number</label>
                  <input
                    type="tel"
                    className={`form-control ${
                      errors.mobile ? "is-invalid" : ""
                    }`}
                    placeholder="Enter mobile number"
                    maxLength={10}
                    {...register("mobile")}
                    onInput={(e) => {
                      e.target.value = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 10);
                    }}
                  />
                  {errors.mobile && (
                    <div className="invalid-feedback">
                      {errors.mobile.message}
                    </div>
                  )}
                </div>

                <div className="col-md-6">
                  <label className="mb-1">Password</label>
                  <input
                    type="password"
                    className={`form-control ${
                      errors.password ? "is-invalid" : ""
                    }`}
                    {...register("password")}
                    placeholder="Enter password"
                  />
                  {errors.password && (
                    <div className="invalid-feedback">
                      {errors.password.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="my-3 d-flex justify-content-center">
                <ReCAPTCHA
                  sitekey="6LfE8EgsAAAAANyUHlqJ_RMe1Klg_WVpVx7NimPG" //hirelink
                  // sitekey="6LdU2HQsAAAAAGrvguav-Xg6Cmn9UoM0D-lymAyJ" //pharmajobshirelink
                  onChange={(token) => setCaptchaToken(token)}
                  onExpired={() => setCaptchaToken(null)}
                />
              </div>

              {/* <div className="mt-2">
                <label className="small fw-medium d-block ">Signup as</label>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    disabled
                    className={`role-pill ${activeRole === "candidate" ? "active" : ""}`}
                  >
                    Candidate
                  </button>
                  {activeRole === "candidate" ? (
                    <button
                      type="button"
                      style={{ border: "none" }}
                      className="me-2"
                      onClick={() => setShowVideo(true)}
                    >
                      <i className="fa fa-circle-info fs-4 text-dark"></i>
                    </button>
                  ) : (
                    ""
                  )}

                  <button
                    type="button"
                    disabled
                    className={`role-pill ${activeRole === "employer" ? "active" : ""}`}
                  >
                    Employer / Recruiter
                  </button>
                </div>
              </div> */}

              {/* SIGNUP AS */}
              <div className="mt-2">
                <label className="small fw-medium d-block mb-2">
                  Signup as
                </label>

                <div className="signup-role-wrap">
                  {/* CANDIDATE */}
                  <button
                    type="button"
                    disabled
                    className={`role-pill ${
                      activeRole === "candidate" ? "active" : ""
                    }`}
                  >
                    Candidate
                  </button>

                  {/* INFO BUTTON */}
                  {activeRole === "candidate" && (
                    <button
                      type="button"
                      className="signup-info-btn"
                      onClick={() => setShowVideo(true)}
                    >
                      <i className="fa fa-circle-info"></i>
                    </button>
                  )}

                  {/* EMPLOYER */}
                  <button
                    type="button"
                    disabled
                    className={`role-pill ${
                      activeRole === "employer" ? "active" : ""
                    }`}
                  >
                    Employer / Recruiter
                  </button>
                </div>
              </div>

              {/* TERMS & CONDITIONS */}
              <div className="mt-2">
                <div className="form-check mb-1">
                  <input
                    className={`form-check-input ${errors?.terms ? "is-invalid" : ""}`}
                    type="checkbox"
                    id="terms"
                    {...register("terms")}
                  />

                  <label className="form-check-label ms-1" htmlFor="terms">
                    I agree to{" "}
                    <NavLink
                      to={termsRoute}
                      target="_blank"
                      className="fw-semibold text-decoration-underline"
                    >
                      {termsLabel}
                    </NavLink>
                  </label>
                </div>

                {errors?.terms && (
                  <small
                    className="team-error ms-1 d-block"
                    style={{ marginTop: "0px" }}
                  >
                    {errors.terms.message}
                  </small>
                )}
              </div>

              <button
                type="submit"
                className="btn btn-primary-signup w-100 mt-1 d-flex justify-content-center align-items-center gap-2"
                disabled={loading || !captchaToken}
              >
                {loading && (
                  <span className="spinner-border spinner-border-sm"></span>
                )}
                {loading ? "Creating account....." : "Create Account"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default Signup;
